import './PaymentLinkOnlyComponent.tsx.css';

import '../../../../js/loadSyncfusion';

// Removed RTE imports
// Removed date-fns imports
// Removed unused model imports (PersonDto, ReservationPerson, getDisplayName)
import { multiplyAndRemoveDecimal, ReservationState } from "@digitalopsdev/serviceops-reservations-components/dist/EditReservation/model"; // Removed PaymentLinkType import
// Removed OrderDetailsComponent imports
// Removed ReservationStatus imports
import { useState, useEffect, useCallback, KeyboardEvent } from 'react'; // Added KeyboardEvent
// Removed utils imports (countProgramDaysBetween, longToMoney)
import React from 'react';
import { useModelState } from '../../../../js/useModelState';
import { getCustomerContactInfo, CustomerContactInfo } from './getCustomerContactInfo';
import { getFullPaymentFeePercent, getPartialPaymentFeePercent } from './paymentFeeRates';

// Define enum locally
enum PaymentLinkType {
    Full = 0,
    Partial = 1,
}

// Define API response structure locally (only single item needed)
interface PaymentLinkResultItem {
    paymentType: PaymentLinkType; // Keep for consistency with API, though we only expect Partial (1)
    url: string | null;
    error: string | null;
}

interface GeneratePaymentLinksResponse {
    paymentLinkResults: PaymentLinkResultItem[]; // API returns array even for single request
}

// --- Renamed Component and Props ---
export function PaymentLinkOnlyComponent({ reservationState }: PaymentLinkOnlyComponentProps) {
    // --- Add AmountDueTotal and AmountPayableTotal model state usage ---
    useModelState(reservationState.AmountDueTotal);
    useModelState(reservationState.AmountPayableTotal);
    // Ensure component re-renders if reservationState.Person changes
    useModelState(reservationState.Person);

    // Status: generatingLink -> ready | error
    const [status, setStatus] = useState<'generatingLink' | 'error' | 'ready'>('ready'); // Start ready if amount due > 0
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // State for link, payment percentage, and provider
    const [paymentLink, setPaymentLink] = useState<string | null>(null);
    const [paymentPercent, setPaymentPercent] = useState<number>(100);
    const [paymentPercentInput, setPaymentPercentInput] = useState<string>("100");
    const [isPaymentPercentValid, setIsPaymentPercentValid] = useState<boolean>(true);
    const [feePercent, setFeePercent] = useState<number>(getPartialPaymentFeePercent('Stripe', 0));
    const [provider, setProvider] = useState<'Omise' | 'Stripe' | '2C2P'>('Stripe');

    // State for customer contact info
    const [customerInfo, setCustomerInfo] = useState<CustomerContactInfo | null>(null);

    const [copied, setCopied] = useState(false);
    const copyLinkToClipboard = useCallback(async () => {
        if (!paymentLink) return;
        try {
            await navigator.clipboard.writeText(paymentLink);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
        } catch {
            // navigator.clipboard requires HTTPS / localhost — silent on failure.
        }
    }, [paymentLink]);

    // Removed bookingDataRef, firstName, richTextContent, rteKey, fullPaymentLink, partialLinkCache

    // --- Fetch Customer Contact Info ---
    useEffect(() => {
        const fetchContactInfo = async () => {
            const person = reservationState.Person.value;
            if (person) {
                const info = await getCustomerContactInfo(person, reservationState);
                setCustomerInfo(info);
            } else {
                setCustomerInfo(null);
            }
        };
        fetchContactInfo();
    }, [reservationState.Person.value, reservationState]);

    // --- Fee Calculation Logic ---
    useEffect(() => {
        if (provider === '2C2P') {
            // 2C2P: fee based on % of total reservation (AmountPayableTotal), not % of balance
            const paymentAmount = reservationState.AmountDueTotal.value * (paymentPercent / 100);
            const percentOfTotal = reservationState.AmountPayableTotal.value > 0
                ? (paymentAmount / reservationState.AmountPayableTotal.value) * 100
                : 0;
            setFeePercent(getPartialPaymentFeePercent(provider, percentOfTotal));
        } else {
            // Stripe/Omise: grossed-up fees
            const amountDueTotalCents = multiplyAndRemoveDecimal(reservationState.AmountDueTotal.value);
            const amountPayableTotalCents = multiplyAndRemoveDecimal(reservationState.AmountPayableTotal.value);
            const isFullPayment = amountDueTotalCents === amountPayableTotalCents && paymentPercent === 100;
            setFeePercent(isFullPayment ? getFullPaymentFeePercent(provider) : getPartialPaymentFeePercent(provider, 0));
        }
    }, [reservationState.AmountDueTotal.value, reservationState.AmountPayableTotal.value, paymentPercent, provider]);

    // --- API Call Helper (Modified) ---
    const callGenerateLinksApi = useCallback(async (
        request: { paymentPercent: number, feePercent: number }, // Accepts single request object
        apiProvider: 'Omise' | 'Stripe' | '2C2P'
    ): Promise<PaymentLinkResultItem | null> => { // Returns single item or null
         const reservationNumber = reservationState.ReservationNumber;
         const totalAmount = reservationState.AmountDueTotal.value; // Use AmountDueTotal directly
         const totalAmountPayable = reservationState.AmountPayableTotal.value; // Use AmountPayableTotal directly

         if (!reservationNumber) { setErrorMessage("Reservation number is missing."); return null; }
         // Use AmountDueTotal for the check, assuming it's the base for percentage
         if (totalAmount <= 0) { setErrorMessage("Total amount due must be positive."); return null; }

         const totalAmountDecimal = totalAmount; // Already a decimal

         try {
             console.log(request.paymentPercent);
             const response = await fetch('/api/reservations/generate-payment-links', {
                 method: 'POST', headers: { 'Content-Type': 'application/json' },
                 body: JSON.stringify({
                     reservationNumber: String(reservationNumber),
                     totalAmount: totalAmountDecimal,
                     // Send single request in the expected array format
                     paymentLinkRequests: [{
                         paymentType: (request.paymentPercent === 100 ? PaymentLinkType.Full : PaymentLinkType.Partial),
                         depositPercent: request.paymentPercent, // Use renamed prop
                         feePercent: request.feePercent,
                         hideDepositText: true
                     }],
                     // Map '2C2P' to '2C2P-SendLink' to use the new GenerateAndSend API
                     provider: apiProvider === '2C2P' ? '2C2P-SendLink' : apiProvider,
                     // Customer info for 2C2P SendLink API
                     customerName: customerInfo?.name || null,
                     customerEmail: customerInfo?.email || null,
                 }),
             });
             if (!response.ok) {
                 const errorText = await response.text();
                 throw new Error(`API Error (${response.status}): ${errorText || 'Failed to generate link'}`);
             }
             const data: GeneratePaymentLinksResponse = await response.json();
             // Expecting one result for our single request
             if (data.paymentLinkResults && data.paymentLinkResults.length > 0) {
                 return data.paymentLinkResults[0];
             } else {
                 throw new Error("API did not return a payment link result.");
             }
         } catch (error: any) {
             console.error("Error calling GeneratePaymentLinks API:", error);
             setErrorMessage(error.message || 'An unexpected error occurred.');
             // Let the caller set the status to 'error'
             return null;
         }
     }, [reservationState.ReservationNumber, reservationState.AmountDueTotal.value, customerInfo]); // Dependencies updated
 
     // --- Function to Generate Payment Link (Modified from generatePartialLink) ---
     // Moved before the useEffect that depends on it
     const generatePaymentLink = useCallback((newPaymentPercent: number) => { // Renamed parameter
        if (status === 'generatingLink') return; // Prevent concurrent requests

        // Compute fee inline to avoid stale closure over feePercent state
        let computedFee: number;
        if (provider === '2C2P') {
            // 2C2P: fee based on % of total reservation (AmountPayableTotal), not % of balance
            const paymentAmount = reservationState.AmountDueTotal.value * (newPaymentPercent / 100);
            const percentOfTotal = reservationState.AmountPayableTotal.value > 0
                ? (paymentAmount / reservationState.AmountPayableTotal.value) * 100
                : 0;
            computedFee = getPartialPaymentFeePercent(provider, percentOfTotal);
        } else {
            const amountDueTotalCents = multiplyAndRemoveDecimal(reservationState.AmountDueTotal.value);
            const amountPayableTotalCents = multiplyAndRemoveDecimal(reservationState.AmountPayableTotal.value);
            const isFullPayment = amountDueTotalCents === amountPayableTotalCents && newPaymentPercent === 100;
            computedFee = isFullPayment ? getFullPaymentFeePercent(provider) : getPartialPaymentFeePercent(provider, 0);
        }

        // Also sync the display state
        setFeePercent(computedFee);

        console.log(`Generating new ${provider} link for ${newPaymentPercent}% with fee ${computedFee}%`);
        setStatus('generatingLink'); // Renamed status
        setPaymentLink(null); // Clear previous link while generating
        setErrorMessage(null); // Clear previous error

        const requestData = { paymentPercent: newPaymentPercent, feePercent: computedFee };

        callGenerateLinksApi(requestData, provider).then(result => { // Expect single result
             if (result) {
                // No need to check paymentType, assuming API returns the requested Partial link
                if (result.error) {
                    setErrorMessage(`Link Generation Error: ${result.error}`);
                    setStatus('error');
                } else if (result.url) {
                    setPaymentLink(result.url); // Set the single payment link state
                    // Removed cache update
                    // Removed RTE generation
                    setErrorMessage(null);
                    setStatus('ready');
                } else {
                     setErrorMessage("Link generation failed silently.");
                     setStatus('error');
                }
             } else {
                  // Error message already set by callGenerateLinksApi in case of fetch/API error
                  setStatus('error');
             }
        });
   }, [callGenerateLinksApi, status, provider, reservationState.AmountDueTotal.value, reservationState.AmountPayableTotal.value]); // Dependencies updated

    // --- Effect to generate link automatically on load if amount is due ---
    // Moved after generatePaymentLink definition
    const generatePaymentLinkCallback = useCallback(generatePaymentLink, [generatePaymentLink, provider]); // Wrap generatePaymentLink for stable dependency
    useEffect(() => {
        // Only run if amount is positive and we are in a ready state (or initial load) and no link exists
        if (reservationState.AmountDueTotal.value > 0 && status === 'ready' && !paymentLink && !errorMessage) {
             console.log("Initial generation triggered by useEffect");
             generatePaymentLinkCallback(paymentPercent); // Use current paymentPercent state (default 100)
        }
        // Dependencies: amount due (to trigger if it becomes positive), status, link existence, error state, and the generation function itself.
    }, [reservationState.AmountDueTotal.value, status, paymentLink, errorMessage, generatePaymentLinkCallback, paymentPercent, provider]);


    // --- Handlers for Input Change, Blur, and KeyDown (Renamed) ---
    const handlePaymentInputChange = (e: React.ChangeEvent<HTMLInputElement>) => { // Renamed handler
        const valueStr = e.target.value;
        setPaymentPercentInput(valueStr); // Update display value immediately
        const valueNum = parseFloat(valueStr);
        if (!isNaN(valueNum) && valueNum >= 0 && valueNum <= 100) {
            setIsPaymentPercentValid(true);
        } else {
            setIsPaymentPercentValid(false);
        }
    };

    const triggerPaymentLinkGeneration = () => { // Renamed helper
        if (!isPaymentPercentValid) return;
        const valueNum = parseFloat(paymentPercentInput);
        if (isNaN(valueNum) || valueNum < 0 || valueNum > 100) { setIsPaymentPercentValid(false); return; }

        // Update the internal percentage state and trigger generation
        setPaymentPercent(valueNum); // Use renamed state setter
        generatePaymentLink(valueNum); // Call renamed generation function
    };

    const handlePaymentInputBlur = () => { // Renamed handler
        triggerPaymentLinkGeneration(); // Call renamed helper
    };

    const handlePaymentInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => { // Renamed handler
        if (e.key === 'Enter') {
            e.preventDefault();
            triggerPaymentLinkGeneration(); // Call renamed helper
            (e.target as HTMLInputElement).blur();
        }
    };


    // --- Render Logic (Modified) ---
    // --- Conditional Rendering (Based on Amount Due) ---
    if (reservationState.AmountDueTotal.value <= 0) {
        return <div className="p-4">No amount due.</div>;
    }

    // Removed loading status check
    const showControls = status === 'ready' || status === 'generatingLink' || status === 'error';
    const isUpdatingLink = status === 'generatingLink'; // Renamed variable

    // Calculate the payment amount based on the percentage
    const paymentAmountCalculated = Math.round(reservationState.AmountDueTotal.value * (paymentPercent / 100) * 100) / 100;

    // Format values for display
    const formatCurrency = (value: number) => value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const totalAmountPayableFormatted = formatCurrency(reservationState.AmountPayableTotal.value);
    const totalAmountDueFormatted = formatCurrency(reservationState.AmountDueTotal.value);
    const paymentAmountCalculatedFormatted = formatCurrency(paymentAmountCalculated);

    return (
        <div className="flex flex-col gap-4 p-4"> {/* Added padding */}
            {/* Error Display */}
            {status === 'error' && (
                 <div className="p-4 text-red-600 border border-red-600 rounded">Error: {errorMessage || 'An unknown error occurred.'}</div>
            )}

            {/* Provider and Payment Input Area */}
            {showControls && (
                <div className="flex flex-wrap gap-4 items-start">
                    {/* Provider Selection */}
                    <div>
                        <label className="text-sm font-semibold">Payment Provider</label>
                        <div className="flex items-center gap-4 mt-1">
                            <label className="flex items-center gap-1 cursor-pointer">
                                <input type="radio" name="provider" value="Stripe" checked={provider === 'Stripe'} onChange={() => { setProvider('Stripe'); setPaymentLink(null); setErrorMessage(null); }} className="form-check-input" disabled={isUpdatingLink} />
                                Stripe
                            </label>
                            <label className="flex items-center gap-1 cursor-pointer">
                                <input type="radio" name="provider" value="Omise" checked={provider === 'Omise'} onChange={() => { setProvider('Omise'); setPaymentLink(null); setErrorMessage(null); }} className="form-check-input" disabled={isUpdatingLink} />
                                Omise
                            </label>
                            <label className="flex items-center gap-1 cursor-pointer">
                                <input type="radio" name="provider" value="2C2P" checked={provider === '2C2P'} onChange={() => { setProvider('2C2P'); setPaymentLink(null); setErrorMessage(null); }} className="form-check-input" disabled={isUpdatingLink} />
                                2C2P
                            </label>
                        </div>
                    </div>

                    {/* Payment Percentage */}
                    <div>
                        <label htmlFor="paymentPercentInput" className="text-sm font-semibold">Payment Percentage</label>
                        <div className="flex items-center gap-2 mt-1">
                            <input
                                id="paymentPercentInput" type="number" min="0" max="100"
                                value={paymentPercentInput}
                                onChange={handlePaymentInputChange}
                                onBlur={handlePaymentInputBlur}
                                onKeyDown={handlePaymentInputKeyDown}
                                className={`form-control w-32 text-right ${!isPaymentPercentValid ? 'border-red-500' : ''} ${isUpdatingLink ? 'opacity-50 cursor-not-allowed' : ''}`}
                                disabled={isUpdatingLink}
                            /> %
                            {isUpdatingLink && <span className="text-blue-600 text-sm ml-2">Generating link...</span>}
                        </div>
                        {!isPaymentPercentValid && <div className="text-red-500 text-sm mt-1">Please enter a percentage between 0 and 100.</div>}
                    </div>
                </div>
            )}

             {/* Amount Display */}
             {showControls && (
                 <div className="text-sm mt-2">
                     <p>Reservation Total: <span className="font-semibold">{totalAmountPayableFormatted}</span></p>
                     <p>Amount Due Total: <span className="font-semibold">{totalAmountDueFormatted}</span></p>
                     <p>Payment ({paymentPercent}%): <span className="font-semibold">{paymentAmountCalculatedFormatted}</span></p>
                 </div>
             )}

             {/* Fee Display — only when a fee actually applies (see paymentFeeRates) */}
             {showControls && feePercent > 0 && (
                 <>
                     <p className="text-sm text-gray-600 mt-1">Applicable Fee: {feePercent}%</p>
                     <p className="text-sm text-gray-600 mt-1">Click Preview Link to see calculated fee</p>
                 </>
             )}

            {/* Preview Button Area (Modified) */}
            {showControls && (
                <div className="flex gap-2 mt-2"> {/* Added margin-top */}
                    {/* Removed "Preview Full Link" button */}
                    {/* Modified remaining button */}
                    <button
                        className="btn btn-sm btn-outline-secondary"
                        // --- Updated onClick to open the single link ---
                        onClick={() => paymentLink && window.open(paymentLink, '_blank')}
                        // --- Updated disabled condition ---
                        disabled={!paymentLink || isUpdatingLink}
                        // --- Updated title ---
                        title={!paymentLink ? "Link not generated or generation failed" : `Open ${paymentPercent}% payment link in new tab`} >
                        Preview Link {/* Changed label */}
                    </button>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={copyLinkToClipboard}
                        disabled={!paymentLink || isUpdatingLink}
                        title={!paymentLink ? "Link not generated or generation failed" : `Copy ${paymentPercent}% payment link to clipboard`}>
                        {copied ? 'Copied' : 'Copy link'}
                    </button>
                </div>
            )}

            {/* Rich Text Editor Removed */}
            {/* Removed generating content placeholder */}
        </div>
    );
}

// --- Renamed Props Interface ---
export interface PaymentLinkOnlyComponentProps {
    reservationState: ReservationState;
}